import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { apiFetch, getEntityTypesList } from "../../services/api";
import SystemInput from "../../components/SystemInput";
import {
  MAP_TILE_GROUPS,
  MAP_TILE_HINTS,
  MAP_TILE_ORDER_INDEX,
} from "../../utils/typeProfile";
import { userCanContribute } from "../../utils/userRoles";
import {
  clearKnowledgePrefill,
  clearOpenAddPanel,
  consumeKeepKnowledgePrefill,
  consumeOpenAddPanel,
} from "../../utils/knowledgePrefill";
import { emitGuideEvent } from "../guide/guideEvents";
import { useDeviceDetection } from "../../hooks/useDeviceDetection";
import { useV2Theme } from "../theme/ThemeProvider";
import { useMobileChrome } from "../mobile/MobileChromeContext";
import MobileBottomSheet from "../components/mobile/MobileBottomSheet";
import { V2_ROUTES } from "../state/useV2Navigation";
import { v2cx } from "../theme/tokens";
import { scrollShellMainToTop } from "../utils/scrollShell";

const V2ExplorerPage = lazy(() =>
  import("./V2ExplorerPage").then((mod) => ({
    default: mod.default ?? mod.V2ExplorerPage,
  }))
);
const V2KnowledgeLibraryPage = lazy(() =>
  import("./V2KnowledgeLibraryPage").then((mod) => ({
    default: mod.default ?? mod.V2KnowledgeLibraryPage,
  }))
);

const EmbeddedPageFallback = ({ label = "Loading…" }) => {
  const { tokens } = useV2Theme();
  return (
    <div className={`${tokens.surface.card} p-4`}>
      <p className={`text-sm ${tokens.surface.mutedText}`}>{label}</p>
    </div>
  );
};

const formatTypeLabel = (value) =>
  String(value || "")
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

function mapGalleryTiles(typeProfiles, countsPayload, { includePending = false } = {}) {
  const counts = countsPayload?.counts || {};
  const proposedCounts = countsPayload?.proposed_counts || {};
  return (Array.isArray(typeProfiles) ? typeProfiles : [])
    .map((profile) => {
      const key = String(profile?.value || "").trim();
      const approvedCount = Number(counts[key] || 0);
      const pendingCount = includePending ? Number(proposedCounts[key] || 0) : 0;
      const totalCount = approvedCount + pendingCount;
      const pendingLabel =
        pendingCount > 0
          ? `${pendingCount.toLocaleString()} pending approval`
          : null;

      const description = MAP_TILE_HINTS[key] || "Items of this type on your map";

      return {
        id: key,
        title: profile?.label || formatTypeLabel(key),
        icon: profile?.icon || "o",
        color: profile?.color || "#64748b",
        count: totalCount,
        approvedCount,
        pendingCount,
        pendingLabel,
        note: description,
        sortIndex: MAP_TILE_ORDER_INDEX[key] ?? 999,
      };
    })
    .filter((tile) => Boolean(tile.id))
    .sort((a, b) => a.sortIndex - b.sortIndex || b.count - a.count);
}

function groupGalleryTiles(tiles) {
  const tileById = Object.fromEntries(tiles.map((tile) => [tile.id, tile]));
  const groupedIds = new Set();

  const groups = MAP_TILE_GROUPS.map((group) => ({
    ...group,
    tiles: group.types.map((typeId) => tileById[typeId]).filter(Boolean),
  })).filter((group) => group.tiles.length > 0);

  groups.forEach((group) => {
    group.tiles.forEach((tile) => groupedIds.add(tile.id));
  });

  const ungrouped = tiles.filter((tile) => !groupedIds.has(tile.id));
  if (ungrouped.length > 0) {
    groups.push({
      id: "other",
      title: "Other",
      hint: "Additional item types on your map",
      tiles: ungrouped,
    });
  }

  return groups;
}

/** Three equal columns: Business | Applications+Data+Connections | Technology. */
const MIDDLE_COLUMN_GROUP_IDS = ["applications", "data", "connections"];

const V2ArchitectureGalleryPage = ({ onNavigate = null }) => {
  const { tokens } = useV2Theme();
  const { isMobile } = useDeviceDetection();
  const { setChrome } = useMobileChrome();
  const canContribute = userCanContribute();
  const [tiles, setTiles] = useState([]);
  const [totalEntities, setTotalEntities] = useState(0);
  const [totalPendingEntities, setTotalPendingEntities] = useState(0);
  const [totalSubmittablePending, setTotalSubmittablePending] = useState(0);
  const [totalRelationships, setTotalRelationships] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [reloadToken, setReloadToken] = useState(0);
  const [selectedType, setSelectedType] = useState(null);
  const [openExplorerCreate, setOpenExplorerCreate] = useState(false);
  const [openExplorerSearch, setOpenExplorerSearch] = useState(false);
  const [openExplorerProposed, setOpenExplorerProposed] = useState(false);
  const [openKnowledgeLibrary, setOpenKnowledgeLibrary] = useState(false);
  const [openAddKnowledge, setOpenAddKnowledge] = useState(false);
  const [addKnowledgeKey, setAddKnowledgeKey] = useState(0);
  const [mobileActionsOpen, setMobileActionsOpen] = useState(false);
  const [librarySummary, setLibrarySummary] = useState(null);
  const [librarySummaryLoading, setLibrarySummaryLoading] = useState(false);

  const showEmbeddedExplorer =
    selectedType != null || openExplorerCreate || openExplorerSearch || openExplorerProposed;

  const showEmbeddedKnowledgeLibrary = openKnowledgeLibrary && !showEmbeddedExplorer;
  const showAddKnowledgePanel =
    openAddKnowledge && !showEmbeddedExplorer && !showEmbeddedKnowledgeLibrary;

  const openAddKnowledgeInFlightRef = useRef(false);
  const openAddKnowledgeFlow = useCallback(() => {
    // Mount + dp-open-map-add can both fire; only the first should clear/keep prefill.
    if (openAddKnowledgeInFlightRef.current) {
      setOpenAddKnowledge(true);
      return;
    }
    openAddKnowledgeInFlightRef.current = true;
    // Fresh opens start on step 1; guide/entity deep-links keep their prefill.
    if (!consumeKeepKnowledgePrefill()) {
      clearKnowledgePrefill();
    }
    setMobileActionsOpen(false);
    setSelectedType(null);
    setOpenExplorerCreate(false);
    setOpenExplorerSearch(false);
    setOpenExplorerProposed(false);
    setOpenKnowledgeLibrary(false);
    setOpenAddKnowledge(true);
    setAddKnowledgeKey((n) => n + 1);
    window.setTimeout(() => {
      openAddKnowledgeInFlightRef.current = false;
    }, 0);
  }, []);

  // Deep link / More menu / guide / explorer: open Add knowledge on Map
  useEffect(() => {
    const tryOpen = () => {
      let fromMap = false;
      try {
        fromMap = sessionStorage.getItem("dp_open_add_from_map") === "1";
        if (fromMap) sessionStorage.removeItem("dp_open_add_from_map");
      } catch {
        /* ignore */
      }
      if (fromMap || consumeOpenAddPanel()) {
        clearOpenAddPanel();
        openAddKnowledgeFlow();
      }
    };
    tryOpen();
    const onOpen = () => openAddKnowledgeFlow();
    window.addEventListener("dp-open-map-add", onOpen);
    return () => window.removeEventListener("dp-open-map-add", onOpen);
  }, [openAddKnowledgeFlow]);

  // Keep product guide off-path detection in sync with Map Add knowledge.
  useEffect(() => {
    emitGuideEvent("map-add-panel", { open: showAddKnowledgePanel });
  }, [showAddKnowledgePanel]);

  // Mobile shell: Map title + ⋯ actions. Re-assert after paint so the shell's
  // route baseline (which clears trailing) cannot win the race — same pattern
  // as Ask. That race is why ⋯ only showed after drill-in → back.
  useEffect(() => {
    if (!isMobile) return undefined;
    // Embedded explorer owns chrome while open.
    if (showEmbeddedExplorer) return undefined;

    let cancelled = false;
    const apply = () => {
      if (cancelled) return;
      if (showAddKnowledgePanel) {
        setChrome({
          chromeMode: "tabs",
          contextTitle: "Add knowledge",
          onBack: () => setOpenAddKnowledge(false),
          backLabel: "Map",
          headerTrailing: null,
          actionTray: null,
          hideShellHeader: false,
        });
        return;
      }
      if (showEmbeddedKnowledgeLibrary) {
        setChrome({
          chromeMode: "tabs",
          contextTitle: "Knowledge library",
          onBack: () => setOpenKnowledgeLibrary(false),
          backLabel: "Map",
          headerTrailing: null,
          actionTray: null,
          hideShellHeader: false,
        });
        return;
      }
      setChrome({
        chromeMode: "tabs",
        contextTitle: "Your map",
        contextSubtitle: "",
        onBack: null,
        backLabel: "",
        headerTrailing: canContribute ? (
          <button
            type="button"
            onClick={() => setMobileActionsOpen(true)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl text-slate-600 active:bg-slate-100"
            aria-label="Map actions"
            title="Map actions"
          >
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
              <circle cx="10" cy="4" r="1.5" />
              <circle cx="10" cy="10" r="1.5" />
              <circle cx="10" cy="16" r="1.5" />
            </svg>
          </button>
        ) : (
          <button
            type="button"
            onClick={() => {
              setSelectedType(null);
              setOpenExplorerCreate(false);
              setOpenExplorerSearch(true);
              setOpenExplorerProposed(false);
            }}
            className={`${tokens.button.secondary} !px-2.5 text-xs`}
          >
            Search
          </button>
        ),
        actionTray: null,
        hideShellHeader: false,
      });
    };
    apply();
    const t = window.setTimeout(apply, 0);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [
    isMobile,
    showEmbeddedExplorer,
    showAddKnowledgePanel,
    showEmbeddedKnowledgeLibrary,
    canContribute,
    setChrome,
    tokens.button.secondary,
  ]);

  useEffect(() => {
    if (showEmbeddedExplorer || showEmbeddedKnowledgeLibrary) return undefined;

    let cancelled = false;
    setRefreshing(true);
    setError("");

    (async () => {
      try {
        const [typeProfiles, countsResponse] = await Promise.all([
          getEntityTypesList(true),
          apiFetch("/api/entity-types/counts?approved_only=true", {
            cache: "no-store",
            timeout: 120000,
          }),
        ]);
        if (cancelled) return;
        if (!countsResponse?.ok) {
          throw new Error(
            `Failed to load entity counts (${countsResponse?.status || "unknown"})`
          );
        }
        const countsPayload = await countsResponse.json();
        setTiles(
          mapGalleryTiles(typeProfiles, countsPayload, {
            includePending: canContribute,
          })
        );
        setTotalEntities(Number(countsPayload?.total_entities || 0));
        setTotalPendingEntities(
          canContribute ? Number(countsPayload?.total_proposed_entities || 0) : 0
        );
        setTotalSubmittablePending(
          canContribute
            ? Number(countsPayload?.total_submittable_proposed_entities || 0)
            : 0
        );
        setTotalRelationships(Number(countsPayload?.total_relationships || 0));
      } catch (err) {
        if (cancelled) return;
        setError(err?.message || "Could not load your map.");
      } finally {
        if (!cancelled) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [showEmbeddedExplorer, showEmbeddedKnowledgeLibrary, reloadToken, canContribute]);

  useEffect(() => {
    if (showEmbeddedExplorer || showEmbeddedKnowledgeLibrary) return undefined;
    let cancelled = false;
    setLibrarySummaryLoading(true);
    (async () => {
      try {
        const { api } = await import("../../services/api");
        const summary = await api.getKnowledgeLibrarySummary();
        if (!cancelled) setLibrarySummary(summary || null);
      } catch {
        if (!cancelled) setLibrarySummary(null);
      } finally {
        if (!cancelled) setLibrarySummaryLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showEmbeddedExplorer, showEmbeddedKnowledgeLibrary, reloadToken]);

  const refresh = useCallback(() => {
    setReloadToken((value) => value + 1);
  }, []);

  const gallerySubtitle = canContribute
    ? "Pick a category to browse or add items."
    : "Pick a category to browse.";

  const tileGroups = useMemo(() => groupGalleryTiles(tiles), [tiles]);

  const businessGroup = tileGroups.find((g) => g.id === "business") || null;
  const technologyGroup = tileGroups.find((g) => g.id === "technology") || null;
  const middleGroups = tileGroups.filter((g) =>
    MIDDLE_COLUMN_GROUP_IDS.includes(g.id)
  );
  const extraGroups = tileGroups.filter(
    (g) =>
      g.id !== "business" &&
      g.id !== "technology" &&
      !MIDDLE_COLUMN_GROUP_IDS.includes(g.id)
  );

  const renderGroupCard = (group, { extraClass = "" } = {}) => (
    <section
      key={group.id}
      className={v2cx(
        tokens.surface.card,
        "flex h-full min-w-0 flex-col",
        extraClass,
        isMobile ? "p-2" : "p-2.5"
      )}
    >
      <div className="mb-1.5 border-b border-slate-200 pb-1">
        <h3 className="text-sm font-semibold text-slate-800">{group.title}</h3>
        <p className={`text-[11px] leading-snug mt-0.5 ${tokens.surface.mutedText}`}>
          {group.hint}
        </p>
      </div>
      <div className="flex flex-1 flex-col gap-1">
        {group.tiles.map((tile) => (
          <button
            key={tile.id}
            type="button"
            onClick={() => openTile(tile.id)}
            className={v2cx(
              "h-[3.75rem] rounded-md border border-slate-200 bg-white text-left transition hover:border-slate-300 hover:bg-slate-50",
              isMobile ? "px-2 py-1" : "px-2.5 py-1"
            )}
          >
            <div className="flex flex-col gap-1">
              <div className="flex items-start justify-between gap-2">
                <p className="min-w-0 flex-1 text-sm font-medium leading-tight text-slate-800">
                  {tile.title}
                </p>
                <p className="shrink-0 text-base font-semibold tabular-nums text-slate-900">
                  {tile.count.toLocaleString()}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <p className={`min-w-0 flex-1 text-[11px] leading-snug ${tokens.surface.mutedText}`}>
                  {tile.note}
                </p>
                {tile.pendingLabel ? (
                  <span className="shrink-0 px-1.5 py-0.5 text-[10px] rounded-full border whitespace-nowrap border-amber-300 bg-amber-50 text-amber-900">
                    {tile.pendingLabel}
                  </span>
                ) : null}
              </div>
            </div>
          </button>
        ))}
      </div>
    </section>
  );

  const openKnowledgeLibraryView = () => {
    scrollShellMainToTop();
    setSelectedType(null);
    setOpenExplorerCreate(false);
    setOpenExplorerSearch(false);
    setOpenExplorerProposed(false);
    setOpenKnowledgeLibrary(true);
  };

  const openTile = (typeId) => {
    scrollShellMainToTop();
    setOpenExplorerCreate(false);
    setOpenExplorerSearch(false);
    setOpenExplorerProposed(false);
    setOpenKnowledgeLibrary(false);
    setSelectedType(typeId);
  };

  const handleBackToGallery = useCallback(() => {
    setSelectedType(null);
    setOpenExplorerCreate(false);
    setOpenExplorerSearch(false);
    setOpenExplorerProposed(false);
  }, []);

  const summaryValue = (value) =>
    loading && tiles.length === 0 ? (
      <span className={`font-normal ${tokens.surface.mutedText}`}>…</span>
    ) : (
      value.toLocaleString()
    );

  // Desktop: free-flow like Inbox (page grows; <main> scrolls). Mobile: contained.
  return (
    <section
      className={
        isMobile
          ? "flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
          : "dp-no-x-overflow min-w-0"
      }
    >
      {showAddKnowledgePanel ? (
        <section
          className={
            isMobile
              ? "flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
              : "dp-no-x-overflow min-w-0 space-y-2"
          }
        >
          <button
            type="button"
            onClick={() => setOpenAddKnowledge(false)}
            className="mb-2 hidden items-center gap-1.5 text-sm font-medium text-blue-700 md:inline-flex"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to your map
          </button>
          <div
            className={v2cx(
              tokens.surface.card,
              isMobile && "flex min-h-0 flex-1 flex-col overflow-hidden"
            )}
          >
            <div className="hidden shrink-0 border-b border-slate-200 px-4 py-2.5 md:block">
              <h2 className="text-base font-semibold text-slate-900">Add knowledge</h2>
            </div>
            <div
              className={
                isMobile
                  ? "min-h-0 flex-1 overflow-y-auto v2-explorer-light px-3 py-2.5 pb-5"
                  : "v2-explorer-light px-4 py-3 pb-6"
              }
            >
              <SystemInput
                key={`map-add-knowledge-${addKnowledgeKey}`}
                onNavigateToGraph={
                  onNavigate ? () => onNavigate(V2_ROUTES.explorer) : undefined
                }
                onNavigateToInbox={
                  onNavigate ? () => onNavigate(V2_ROUTES.insightsInbox) : undefined
                }
              />
            </div>
          </div>
        </section>
      ) : null}

      {showEmbeddedKnowledgeLibrary ? (
        <div
          className={
            isMobile
              ? "flex min-h-0 flex-1 flex-col overflow-hidden"
              : "dp-no-x-overflow min-w-0"
          }
        >
          <Suspense
            fallback={
              <EmbeddedPageFallback label="Loading reference knowledge…" />
            }
          >
            <V2KnowledgeLibraryPage
              onBackToGallery={() => setOpenKnowledgeLibrary(false)}
              onNavigate={onNavigate}
            />
          </Suspense>
        </div>
      ) : null}

      {showEmbeddedExplorer ? (
        <div
          className={
            isMobile
              ? "dp-no-x-overflow flex min-h-0 min-w-0 flex-1 flex-col overflow-y-hidden"
              : // Bound the embedded Explorer to the viewport (shell header +
                // main padding ≈ 9.5rem) so fill-height children — the Graph —
                // use the full vertical space instead of collapsing to content.
                "dp-no-x-overflow flex h-[calc(100dvh-9.5rem)] min-w-0 flex-col"
          }
        >
          <Suspense fallback={<EmbeddedPageFallback label="Loading explorer…" />}>
            <V2ExplorerPage
              key={
                selectedType ||
                (openExplorerCreate
                  ? "__create__"
                  : openExplorerSearch
                    ? "__search__"
                    : openExplorerProposed
                      ? "__proposed__"
                      : "explorer")
              }
              initialEntityTypeFilter={selectedType || "all"}
              initialOpenCreate={Boolean(openExplorerCreate && !selectedType)}
              initialFocusSearch={Boolean(openExplorerSearch && !selectedType && !openExplorerCreate)}
              initialProposedOnly={Boolean(openExplorerProposed && !selectedType)}
              initialSelectionMode={Boolean(openExplorerProposed && !selectedType)}
              onNavigate={onNavigate}
              onBackToGallery={handleBackToGallery}
            />
          </Suspense>
        </div>
      ) : null}

      {!showEmbeddedExplorer && !showEmbeddedKnowledgeLibrary && !showAddKnowledgePanel ? (
        <div
          className={v2cx(
            isMobile
              ? "min-h-0 flex-1 space-y-3 overflow-x-hidden overflow-y-auto overscroll-y-contain"
              : "space-y-2.5"
          )}
        >
      <div>
        <div className="flex items-center justify-between gap-2 sm:gap-3">
          <h2 className="hidden truncate text-lg font-semibold tracking-tight md:block sm:text-2xl">Your map</h2>
          {/* Desktop action cluster; mobile uses shell + sheet */}
          <div className="hidden shrink-0 flex-wrap items-center justify-end gap-1.5 md:flex sm:gap-2">
          {canContribute ? (
            <button
              type="button"
              onClick={openAddKnowledgeFlow}
              className={`${tokens.button.success} whitespace-nowrap !px-2.5 sm:!px-3 text-xs sm:text-sm`}
            >
              Add knowledge
            </button>
          ) : null}
          {canContribute ? (
            <button
              type="button"
              onClick={() => {
                scrollShellMainToTop();
                setOpenExplorerCreate(true);
                setOpenExplorerSearch(false);
                setOpenExplorerProposed(false);
              }}
              className={`${tokens.button.secondary} whitespace-nowrap !px-2.5 sm:!px-3 text-xs sm:text-sm`}
            >
              Add item
            </button>
          ) : null}
          {canContribute && totalSubmittablePending > 0 ? (
            <button
              type="button"
              onClick={() => {
                scrollShellMainToTop();
                setSelectedType(null);
                setOpenExplorerCreate(false);
                setOpenExplorerSearch(false);
                setOpenExplorerProposed(true);
              }}
              className={`${tokens.button.secondary} whitespace-nowrap !px-2.5 sm:!px-3 text-xs sm:text-sm`}
            >
              Submit for approval
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => {
              scrollShellMainToTop();
              setSelectedType(null);
              setOpenExplorerCreate(false);
              setOpenExplorerSearch(true);
              setOpenExplorerProposed(false);
            }}
            className={`${tokens.button.secondary} whitespace-nowrap !px-2.5 sm:!px-3 text-xs sm:text-sm`}
          >
            Search
          </button>
          <button
            type="button"
            onClick={refresh}
            disabled={refreshing}
            className={`${tokens.button.primary} inline-flex items-center gap-1.5 whitespace-nowrap !px-2.5 sm:!px-3 text-xs sm:text-sm disabled:opacity-50`}
            title={refreshing ? "Refreshing…" : "Refresh"}
            aria-label={refreshing ? "Refreshing" : "Refresh"}
          >
            <svg
              className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            <span className="hidden sm:inline">
              {refreshing ? "Refreshing…" : "Refresh"}
            </span>
          </button>
          </div>
        </div>
        <p
          className={`${tokens.surface.mutedText} mt-1 text-sm leading-relaxed ${
            isMobile ? "max-w-prose" : ""
          }`}
        >
          {gallerySubtitle}
        </p>
      </div>

      <MobileBottomSheet
        open={mobileActionsOpen}
        onClose={() => setMobileActionsOpen(false)}
        title="Map actions"
        height="half"
      >
        <ul className="space-y-1">
          {canContribute ? (
            <li>
              <button
                type="button"
                className="flex w-full items-center gap-3 rounded-xl px-3 py-3.5 text-left text-sm font-medium text-emerald-800 active:bg-emerald-50"
                onClick={openAddKnowledgeFlow}
              >
                Add knowledge
              </button>
            </li>
          ) : null}
          {canContribute ? (
            <li>
              <button
                type="button"
                className="flex w-full items-center gap-3 rounded-xl px-3 py-3.5 text-left text-sm font-medium text-slate-800 active:bg-slate-100"
                onClick={() => {
                  setMobileActionsOpen(false);
                  scrollShellMainToTop();
                  setOpenExplorerCreate(true);
                  setOpenExplorerSearch(false);
                  setOpenExplorerProposed(false);
                }}
              >
                Add item
              </button>
            </li>
          ) : null}
          {canContribute && totalSubmittablePending > 0 ? (
            <li>
              <button
                type="button"
                className="flex w-full items-center gap-3 rounded-xl px-3 py-3.5 text-left text-sm font-medium text-slate-800 active:bg-slate-100"
                onClick={() => {
                  setMobileActionsOpen(false);
                  scrollShellMainToTop();
                  setSelectedType(null);
                  setOpenExplorerCreate(false);
                  setOpenExplorerSearch(false);
                  setOpenExplorerProposed(true);
                }}
              >
                Submit for approval
              </button>
            </li>
          ) : null}
          <li>
            <button
              type="button"
              className="flex w-full items-center gap-3 rounded-xl px-3 py-3.5 text-left text-sm font-medium text-slate-800 active:bg-slate-100"
              onClick={() => {
                setMobileActionsOpen(false);
                scrollShellMainToTop();
                setSelectedType(null);
                setOpenExplorerCreate(false);
                setOpenExplorerSearch(true);
                setOpenExplorerProposed(false);
              }}
            >
              Search
            </button>
          </li>
          <li>
            <button
              type="button"
              className="flex w-full items-center gap-3 rounded-xl px-3 py-3.5 text-left text-sm font-medium text-slate-800 active:bg-slate-100"
              disabled={refreshing}
              onClick={() => {
                setMobileActionsOpen(false);
                refresh();
              }}
            >
              {refreshing ? "Refreshing…" : "Refresh"}
            </button>
          </li>
        </ul>
      </MobileBottomSheet>

      <div
        className={v2cx(
          tokens.surface.card,
          "flex flex-wrap items-center gap-x-6 gap-y-1 text-sm",
          isMobile ? "gap-x-4 px-3 py-2.5" : "px-4 py-1.5"
        )}
      >
        <p className={tokens.surface.subtleText}>
          On your map{" "}
          <span className="ml-1 font-semibold text-slate-900 tabular-nums">
            {summaryValue(totalEntities)}
          </span>
        </p>
        {canContribute ? (
          <p className={tokens.surface.subtleText}>
            Pending{" "}
            <span className="ml-1 font-semibold text-slate-900 tabular-nums">
              {summaryValue(totalPendingEntities)}
            </span>
          </p>
        ) : null}
        <p className={tokens.surface.subtleText}>
          Connections{" "}
          <span className="ml-1 font-semibold text-slate-900 tabular-nums">
            {summaryValue(totalRelationships)}
          </span>
        </p>
      </div>

      <div className="grid grid-cols-1 items-start gap-2.5 md:grid-cols-2 xl:grid-cols-3">
        {loading && tiles.length === 0 ? (
          <div
            className={v2cx(
              tokens.surface.card,
              "md:col-span-2 xl:col-span-3",
              isMobile ? "p-3" : "p-4"
            )}
          >
            <p className={`text-sm ${tokens.surface.mutedText}`}>Loading map types…</p>
          </div>
        ) : tiles.length === 0 ? (
          <div
            className={v2cx(
              tokens.surface.card,
              "md:col-span-2 xl:col-span-3",
              isMobile ? "p-3" : "p-4"
            )}
          >
            <p className={`text-sm ${tokens.surface.mutedText}`}>No map types found.</p>
          </div>
        ) : (
          <>
            {businessGroup ? renderGroupCard(businessGroup) : null}
            {middleGroups.length ? (
              <div className="flex min-w-0 flex-col gap-2.5">
                {middleGroups.map((group) =>
                  renderGroupCard(group, { extraClass: "flex-1" })
                )}
              </div>
            ) : null}
            {technologyGroup
              ? renderGroupCard(technologyGroup, {
                  extraClass: "md:col-span-2 xl:col-span-1",
                })
              : null}
            {extraGroups.map((group) => renderGroupCard(group))}
          </>
        )}
      </div>

      <section className={`${tokens.surface.card} hidden p-2.5 md:block`}>
        <div className="flex flex-col gap-2 md:flex-row md:items-center">
          <div className="min-w-0 shrink-0 md:max-w-xs">
            <h3 className="text-sm font-semibold text-slate-800">Reference knowledge</h3>
            <p className={`text-[11px] leading-snug mt-0.5 ${tokens.surface.mutedText}`}>
              Principles, patterns, SOPs, and articles — indexed for search, not on your map.
            </p>
          </div>
          <button
            type="button"
            onClick={openKnowledgeLibraryView}
            className="w-full flex-1 rounded-md border border-violet-200 bg-violet-50/60 px-3 py-1.5 text-left transition hover:border-violet-300 hover:bg-violet-50"
          >
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900">Knowledge library</p>
                <p className={`text-[11px] leading-snug mt-0.5 ${tokens.surface.mutedText}`}>
                  {librarySummaryLoading
                    ? "Loading categories…"
                    : `${Number(librarySummary?.surface_count || 0).toLocaleString()} categories · ${Number(librarySummary?.surfaces_with_sources || 0).toLocaleString()} with sources`}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <p className="text-base font-semibold tabular-nums text-slate-900">
                  {librarySummaryLoading
                    ? "…"
                    : Number(librarySummary?.source_count || 0).toLocaleString()}
                </p>
                <span className="px-1.5 py-0.5 text-[10px] rounded-full border whitespace-nowrap border-violet-200 bg-white text-violet-800">
                  {librarySummaryLoading
                    ? "…"
                    : librarySummary?.has_content
                      ? "Has content"
                      : "Empty"}
                </span>
              </div>
            </div>
          </button>
        </div>
      </section>

      {error ? <p className="text-sm text-red-500">{error}</p> : null}
        </div>
      ) : null}
    </section>
  );
};

export default V2ArchitectureGalleryPage;
